import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { ArrowUpRight, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback, solange im Admin nichts gepflegt ist
const defaultServiceCategories = [
  {
    id: 'creative-production',
    title: 'Creative Production',
    description: 'Konzeption, Foto, Video und Postproduktion für starke visuelle Markenauftritte.',
    items: [
      { title: 'Commercials & Brand Films', description: 'Kinoreife Werbefilme und Imagefilme mit klarer Erzählung.' },
      { title: 'Foto- & Videoproduktion', description: 'Kampagne, Portrait, Produkt und Editorial aus einer Hand.' },
      { title: 'Regie & Kamera', description: 'Bildsprache, Licht und Führung am Set.' },
      { title: 'Schnitt & Color Grading', description: 'Postproduktion bis zum auslieferfertigen Master.' },
    ],
  },
  {
    id: 'monthly-direction',
    title: 'Monthly Direction',
    description: 'Kontinuierliche Produktion und Betreuung für Marken, die konstant sichtbar bleiben wollen.',
    items: [
      { title: 'Feste Produktionstage', description: 'Planbare Drehtage im Monatsrhythmus.' },
      { title: 'Redaktionsplanung', description: 'Themen, Formate und Timing im Voraus abgestimmt.' },
      { title: 'Laufende Betreuung', description: 'Fester Ansprechpartner statt Projektchaos.' },
      { title: 'Reporting & Optimierung', description: 'Auswertung und Nachschärfen jeden Monat.' },
    ],
  },
  {
    id: 'social-strategy',
    title: 'Social & Strategy',
    description: 'Analyse, Redaktionsplanung und kreative Umsetzung auf Basis klarer Ziele.',
    items: [
      { title: 'Analyse & Zielsetzung', description: 'Wo steht die Marke, was soll erreicht werden.' },
      { title: 'Content-Strategie', description: 'Formate und Botschaften pro Kanal.' },
      { title: 'Social Content', description: 'Hochformat, Quadrat und Querformat als Serie gedacht.' },
      { title: 'Ausspielung & Community', description: 'Veröffentlichung, Timing und Betreuung.' },
    ],
  },
  {
    id: 'websites',
    title: 'Websites',
    description: 'Websites, die zur Marke passen: schnell, klar aufgebaut und selbst pflegbar.',
    items: [
      { title: 'Konzept & Struktur', description: 'Seitenaufbau, Nutzerführung und Inhalte.' },
      { title: 'Design & Umsetzung', description: 'Individuelles Design, sauber umgesetzt.' },
      { title: 'Responsive & schnell', description: 'Auf jedem Gerät gut lesbar und schnell geladen.' },
      { title: 'Pflegbar im CMS', description: 'Inhalte selbst ändern, ohne Entwickler.' },
    ],
  },
  {
    id: 'grafiken',
    title: 'Grafiken',
    description: 'Grafik und Design für Print und Digital, vom Logo bis zum fertigen Layout.',
    items: [
      { title: 'Logo & Markenauftritt', description: 'Bildmarke, Farben, Schriften und Regeln.' },
      { title: 'Print & Editorial', description: 'Flyer, Broschüren, Plakate und Geschäftsausstattung.' },
      { title: 'Digital Assets', description: 'Grafiken für Social Media, Web und Kampagnen.' },
      { title: 'Motion & Animation', description: 'Bewegte Grafiken, Titel und Logo-Animationen.' },
    ],
  },
];

export default function ServicesPage() {
  const [serviceCategories, setServiceCategories] = useState(defaultServiceCategories);
  const [pageContent, setPageContent] = useState({
    headline: 'PRODUKTION IST\nMEHR ALS EIN\nDREHTAG.',
    description: 'Wir verbinden Idee, Planung, Produktion und Ausspielung zu einem System, das für Marke und Zielgruppe funktioniert.',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, contentRes] = await Promise.all([
        fetch(`${API_URL}/api/service-categories`).catch(() => null),
        fetch(`${API_URL}/api/content`),
      ]);

      if (servicesRes?.ok) {
        const servicesData = await servicesRes.json();
        if (servicesData.length > 0) {
          setServiceCategories(servicesData);
        }
      }

      if (contentRes.ok) {
        const contentData = await contentRes.json();
        const headline = contentData.find((c) => c.key === 'services_headline');
        const description = contentData.find((c) => c.key === 'services_text');
        setPageContent((prev) => ({
          headline: headline?.value || prev.headline,
          description: description?.value || prev.description,
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-foreground/20 border-t-brand rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Lädt</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Kopfbereich */}
      <section className="pt-32 pb-14 md:pt-40 md:pb-20">
        <div className="container-custom">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="kicker mb-6 inline-flex"
          >
            Was wir machen
          </motion.span>
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8 lg:gap-16 items-end">
            <motion.h1
              initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] text-foreground"
            >
              {pageContent.headline.split('\n').map((line, i) => (
                <span key={i} className="block">{line.trim()}</span>
              ))}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="text-base md:text-lg text-foreground/80 max-w-md leading-relaxed"
            >
              {pageContent.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Leistungskarten — nebeneinander, ohne Bilder */}
      <section className="pb-24 md:pb-32">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {serviceCategories.map((category, index) => (
              <Reveal key={category.id} delay={(index % 3) * 0.1}>
                <article className={`service-card h-full ${index % 3 === 1 ? 'dark' : ''}`}>
                  <span className="service-number">{String(index + 1).padStart(2, '0')}</span>

                  <h2 className="font-display text-2xl md:text-3xl mb-4">
                    {category.title}
                  </h2>

                  {category.description && (
                    <p className={`text-sm md:text-base leading-relaxed mb-6 ${index % 3 === 1 ? 'text-white/75' : 'text-muted-foreground'}`}>
                      {category.description}
                    </p>
                  )}

                  {category.items?.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {category.items.map((item, i) => (
                        <li key={i} className="flex gap-3">
                          <Check className={`w-4 h-4 mt-1 shrink-0 ${index % 3 === 1 ? 'text-yellow' : 'text-brand'}`} />
                          <span>
                            <span className={`block text-sm font-semibold ${index % 3 === 1 ? 'text-white' : 'text-foreground'}`}>
                              {item.title}
                            </span>
                            {item.description && (
                              <span className={`block text-sm leading-relaxed ${index % 3 === 1 ? 'text-white/65' : 'text-muted-foreground'}`}>
                                {item.description}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-auto pt-2">
                    <Link
                      to="/#contact"
                      className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                        index % 3 === 1 ? 'text-yellow hover:text-white' : 'text-brand hover:text-foreground'
                      }`}
                    >
                      Projekt anfragen
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* Abschluss-CTA */}
          <Reveal delay={0.15}>
            <div className="mt-10 bg-ink text-white p-8 md:p-10 border-b-4 border-yellow flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h2 className="font-display text-2xl md:text-3xl mb-2">Nicht sicher, was passt?</h2>
                <p className="text-white/70 text-sm">
                  Wir schauen uns dein Vorhaben an und schlagen den passenden Umfang vor.
                </p>
              </div>
              <Link to="/#contact" className="btn-primary shrink-0">
                Projekt starten →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
